import React from 'react'
import { formatMoney } from '../../utils/dealMapper'
import { DEAL_PHASES } from '../../constants/dealPhases'
import { RsIcon, IconLabel } from '../icons'

export { default as PortalGpInProgress } from './PortalGpInProgress'

const STAGE_LABELS = {
  posted: 'Launch',
  admin_review: 'Launch',
  matching: 'Launch',
  nda: 'Due Diligence',
  dd: 'Due Diligence',
  term_sheet: 'Negotiating/SPA',
  escrow: 'About to Deliver',
  closed: 'Post-Delivery',
}

const GP_STAGE_LABELS = STAGE_LABELS

export const GP_IN_PROGRESS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'launch', label: 'Launch', phases: ['posted', 'admin_review', 'matching'] },
  { id: 'dd', label: 'Due Diligence', phases: ['nda', 'dd'] },
  { id: 'negotiation', label: 'Negotiating/SPA', phases: ['term_sheet'] },
  { id: 'delivery', label: 'Delivery', phases: ['escrow'] },
  { id: 'post_delivery', label: 'Post-Delivery', phases: ['closed'] },
]

export const PORTAL_STAGE_FILTERS = GP_IN_PROGRESS_FILTERS

const STAGE_BADGE_CLASS = {
  posted: 'matching',
  admin_review: 'matching',
  matching: 'matching',
  nda: 'nda',
  dd: 'dd',
  term_sheet: 'term_sheet',
  escrow: 'escrow',
  closed: 'closed',
}

const TRACKER_STAGES = [
  { key: 'posted', icon: 'upgrade', label: 'Launch' },
  { key: 'dd', icon: 'search', label: 'Due Diligence' },
  { key: 'term_sheet', icon: 'contracts', label: 'Negotiation / SPA' },
  { key: 'escrow', icon: 'fund', label: 'Closing' },
  { key: 'closed', icon: 'clipboard', label: 'Post-Close' },
]

function dealTypeLabel(deal) {
  const ind = (deal.industry || '').toLowerCase()
  if (/fund|vc|pe/.test(ind)) return { cls: 'fund', label: 'Fund', icon: 'fund' }
  if (/direct|series|startup/.test(ind)) return { cls: 'direct', label: 'Direct', icon: 'deal' }
  if (/co/.test(ind)) return { cls: 'coinvest', label: 'Co-invest', icon: 'coinvest' }
  return { cls: 'secondary', label: 'Secondary', icon: 'archive' }
}

function phaseIndex(phase) {
  return DEAL_PHASES.findIndex((p) => p.key === phase)
}

function participantAvatars(deal) {
  const roles = ['gp', 'lp', 'law', 'audit']
  if (['term_sheet', 'escrow', 'closed'].includes(deal.currentPhase)) roles.push('bank')
  return roles
}

function dealSubtitle(deal) {
  const lpMatch = (deal.description || '').match(/LP[:\s]+([^,\n]+)/i)
  const lp = lpMatch?.[1]?.trim()
  return lp ? `GP: ${deal.gp} · LP: ${lp}` : `GP: ${deal.gp}`
}

function dealDeadline(deal) {
  const phase = deal.currentPhase
  const pct = deal.progress || 0
  if (['posted', 'admin_review', 'matching'].includes(phase)) return 'Launch'
  if (phase === 'escrow' || pct >= 85) {
    return pct >= 90 ? 'Today Delivery' : 'Closing soon'
  }
  if (phase === 'term_sheet') return '5 days left'
  if (phase === 'dd' || phase === 'nda') return '2 weeks left'
  if (phase === 'closed') return 'Delivered'
  return '—'
}

function countDealsForFilter(deals, filterId) {
  if (filterId === 'all') return deals.length
  const spec = GP_IN_PROGRESS_FILTERS.find((f) => f.id === filterId)
  if (!spec?.phases) return 0
  return deals.filter((d) => spec.phases.includes(d.currentPhase)).length
}

function sumTargetAmount(deals) {
  const total = deals.reduce((s, d) => s + Number(d.targetAmount || 0), 0)
  return total
}

export function PortalDealsStats({ pageStats, listMode, dealCount, loading, role }) {
  const items = listMode === 'browse'
    ? [
      { num: pageStats?.activeDeals ?? dealCount, label: 'Open Opportunities' },
      { num: pageStats?.inDiligence ?? 0, label: 'In Diligence' },
      { num: pageStats?.browseTotal ?? '—', label: 'Fund Deals' },
      { num: pageStats?.myPortfolio ?? 0, label: 'In Portfolio' },
    ]
    : role === 'GP'
      ? [
        { num: pageStats?.activeDeals ?? dealCount, label: 'In Progress Deals' },
        { num: pageStats?.pendingActions ?? 0, label: 'Pending Actions', warn: true },
        { num: pageStats?.inDiligence ?? 0, label: 'In Diligence' },
        {
          num: pageStats?.totalEscrowed != null ? formatMoney(pageStats.totalEscrowed) : '—',
          label: 'Total Deal Volume',
          success: true,
        },
      ]
      : role === 'Startup'
        ? [
          { num: pageStats?.pendingActions ?? 0, label: 'Pending Actions', warn: true },
          { num: pageStats?.inDiligence ?? 0, label: 'In Diligence' },
          { num: pageStats?.myPortfolio ?? 0, label: 'In Portfolio', success: true },
        ]
        : [
          { num: pageStats?.activeDeals ?? dealCount, label: 'Active Deals' },
          { num: pageStats?.pendingActions ?? 0, label: 'Pending Actions', warn: true },
          { num: pageStats?.inDiligence ?? 0, label: 'In Diligence' },
          { num: pageStats?.myPortfolio ?? 0, label: 'In Portfolio', success: true },
        ]

  return (
    <div className="portal-deals-stats">
      {items.map((s) => (
        <div key={s.label} className={`portal-deals-stat${s.warn ? ' warning' : ''}${s.success ? ' success' : ''}`}>
          <div className="num">{loading ? '…' : s.num}</div>
          <div className="label">{s.label}</div>
        </div>
      ))}
    </div>
  )
}

export function PortalDealsTable({ deals, onView, gpLayout }) {
  if (!deals.length) {
    return (
      <div className="portal-deal-table portal-deal-table-empty">
        <p>No in-progress deals yet.</p>
      </div>
    )
  }

  const stageLabels = gpLayout ? GP_STAGE_LABELS : STAGE_LABELS

  return (
    <div className="portal-deal-table">
      <div className="portal-deal-table-head">
        <span>Deal Name</span>
        <span>Amount</span>
        <span>{gpLayout ? 'Stage' : 'Current Stage'}</span>
        <span>Progress</span>
        <span>Participants</span>
        <span>Deadline</span>
        <span>{gpLayout ? 'View Details' : 'Actions'}</span>
      </div>
      {deals.map((deal) => {
        const pct = deal.progress || 0
        const urgent = pct >= 80 || deal.currentPhase === 'escrow'
        const deadline = gpLayout ? dealDeadline(deal) : (urgent ? 'Closing soon' : '—')
        const urgentDeadline = gpLayout
          ? (deadline === 'Today Delivery' || deadline === 'Closing soon')
          : urgent
        return (
          <div key={deal.id} className="portal-deal-table-row" onClick={() => onView(deal)}>
            <div>
              <div className="portal-deal-name">
                {deal.title}
                <small>{gpLayout ? dealSubtitle(deal) : `GP: ${deal.gp}${deal.location ? ` · ${deal.location}` : ''}`}</small>
              </div>
            </div>
            <div className="portal-deal-amount">{deal.target || formatMoney(deal.targetAmount)}</div>
            <div className="portal-deal-stage">
              <span className={`portal-stage-badge ${STAGE_BADGE_CLASS[deal.currentPhase] || 'matching'}`}>
                {stageLabels[deal.currentPhase] || (deal.currentPhase || '').replace(/_/g, ' ')}
              </span>
            </div>
            <div className="portal-deal-progress-cell">
              <div className="portal-mini-progress">
                <div
                  className={`portal-mini-progress-fill${urgent ? ' success' : ''}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className={`portal-mini-progress-text${urgent ? ' success' : ''}`}>{pct}%</div>
            </div>
            <div className="portal-deal-participants">
              {participantAvatars(deal).map((r) => (
                <span key={r} className={`portal-participant-avatar ${r}`}>{r.toUpperCase().slice(0, 2)}</span>
              ))}
              <span className="portal-participant-more">+1</span>
            </div>
            <div className={`portal-deal-deadline${urgentDeadline ? ' urgent' : ''}`}>
              {deadline}
            </div>
            <div className="portal-deal-action">
              <button
                type="button"
                className="portal-btn-view"
                onClick={(e) => {
                  e.stopPropagation()
                  onView(deal)
                }}
              >
                View Details
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function PortalBrowseDeals({ deals, onView, onJoin, role }) {
  if (!deals.length) {
    return (
      <div className="portal-deal-opp-empty">
        <p>No opportunities match your filters.</p>
      </div>
    )
  }

  return (
    <div className="portal-deal-opp-list">
      {deals.map((deal) => {
        const type = dealTypeLabel(deal)
        return (
          <div key={deal.id} className="portal-deal-opp-card">
            <div className="portal-deal-opp-icon"><RsIcon name={type.icon} size="md" /></div>
            <div className="portal-deal-opp-body">
              <div className="portal-deal-opp-header">
                <div className="portal-deal-name">{deal.title}</div>
                <span className={`portal-deal-type ${type.cls}`}>{type.label}</span>
              </div>
              <div className="portal-deal-opp-meta">
                <span>$ {deal.target || formatMoney(deal.targetAmount)}</span>
                <span>{deal.location || '—'}</span>
                {deal.industry && <span>{deal.industry}</span>}
              </div>
              {deal.description && (
                <p className="portal-deal-opp-desc">
                  {(deal.description || '').slice(0, 180)}{(deal.description || '').length > 180 ? '…' : ''}
                </p>
              )}
              <div className="portal-deal-opp-footer">
                <div className="portal-deal-tags">
                  <span className="portal-deal-tag">{(deal.currentPhase || 'open').replace(/_/g, ' ')}</span>
                  {deal.isParticipant && <span className="portal-deal-tag active">In portfolio</span>}
                </div>
                <div className="portal-deal-opp-actions">
                  <button
                    type="button"
                    className="portal-btn-view"
                    onClick={(e) => {
                      e.stopPropagation()
                      onView(deal)
                    }}
                  >
                    View
                  </button>
                  {role === 'LP' && !deal.isParticipant && deal.currentPhase !== 'closed' && (
                    <button type="button" className="portal-btn-primary" onClick={() => onJoin(deal.id)}>
                      Express Interest
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function trackerStageStatus(stageKey, currentPhase) {
  const order = ['posted', 'dd', 'term_sheet', 'escrow', 'closed']
  const phaseToStage = {
    posted: 'posted',
    admin_review: 'posted',
    matching: 'posted',
    nda: 'posted',
    dd: 'dd',
    term_sheet: 'term_sheet',
    escrow: 'escrow',
    closed: 'closed',
  }
  const current = phaseToStage[currentPhase] || 'posted'
  const curIdx = order.indexOf(current)
  const stageIdx = order.indexOf(stageKey)
  if (curIdx > stageIdx) return 'completed'
  if (curIdx === stageIdx) return 'active'
  return 'pending'
}

export function PortalDealDetail({ deal, role, listMode, onClose, onChat, onJoin, onCompleteAction }) {
  if (!deal) return null

  const pct = deal.progress || 0
  const currentLabel = STAGE_LABELS[deal.currentPhase] || deal.currentPhase

  return (
    <div className="portal-deal-detail">
      <div className="portal-deal-detail-header">
        <div>
          <h2>{deal.title}</h2>
          <p>{deal.gp} · Target {deal.target} · {currentLabel}</p>
        </div>
        <button type="button" className="portal-btn-view" onClick={onClose}>Close</button>
      </div>

      <div className="portal-overall-progress">
        <div className="portal-overall-stat">
          <p className="num">{pct}%</p>
          <p className="label">Deal Progress</p>
        </div>
        <div className="portal-overall-stat">
          <p className="num">{deal.pendingActions?.length || 0}</p>
          <p className="label">Pending Tasks</p>
        </div>
        <div className="portal-overall-stat">
          <p className="num">{deal.raised || '—'}</p>
          <p className="label">Raised</p>
        </div>
        <div className="portal-overall-bar-wrap">
          <div className="portal-overall-bar-label">
            <span>Overall Progress</span>
            <span>{pct}%</span>
          </div>
          <div className="portal-overall-bar">
            <div className="portal-overall-bar-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <div className="portal-progress-tracker">
        <div className="portal-tracker-header">
          <div className="portal-tracker-title">
            Current Stage: {currentLabel}
          </div>
          <span className="portal-tracker-meta">Phase {Math.min(phaseIndex(deal.currentPhase) + 1, DEAL_PHASES.length)} / {DEAL_PHASES.length}</span>
        </div>
        <div className="portal-tracker-stages">
          {TRACKER_STAGES.map((stage) => {
            const status = trackerStageStatus(stage.key, deal.currentPhase)
            return (
              <div key={stage.key} className={`portal-tracker-stage ${status}`}>
                <div className="portal-tracker-dot"><RsIcon name={stage.icon} size="sm" /></div>
                <div className="portal-tracker-label">{stage.label}</div>
                <div className="portal-tracker-meta">
                  {status === 'completed' ? 'Completed' : status === 'active' ? 'In Progress' : 'Pending'}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="portal-deal-detail-grid">
        <div className="portal-detail-card">
          <div className="portal-detail-card-title">Participants</div>
          <div className="portal-participant-list">
            <div className="portal-participant-item">
              <span className="portal-participant-avatar gp">GP</span>
              <div>
                <p className="name">{deal.gp}</p>
                <p className="role">Deal Sponsor</p>
              </div>
              <span className="portal-status-pill active">Active</span>
            </div>
            <div className="portal-participant-item">
              <span className="portal-participant-avatar lp">LP</span>
              <div>
                <p className="name">Matched LP</p>
                <p className="role">Investor</p>
              </div>
              <span className="portal-status-pill waiting">Pending</span>
            </div>
          </div>
        </div>

        <div className="portal-detail-card">
          <div className="portal-detail-card-title">Pending Tasks</div>
          {deal.pendingActions?.length ? (
            <div className="portal-task-list">
              {deal.pendingActions.map((a) => (
                <div key={a.id} className="portal-task-item in-progress">
                  <div className="portal-task-body">
                    <p className="portal-task-title">{a.title}</p>
                    <p className="portal-task-meta">Assigned: {a.assignedRole || 'GP'}</p>
                  </div>
                  {onCompleteAction && (
                    <button type="button" className="portal-btn-view" onClick={() => onCompleteAction(deal.id, a.id)}>
                      Complete
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="portal-detail-empty">No pending tasks</p>
          )}
        </div>

        <div className="portal-detail-card full">
          <div className="portal-detail-card-title">Activity Timeline</div>
          {deal.timeline?.length ? (
            <div className="portal-activity-list">
              {deal.timeline.map((t, i) => (
                <div key={`${t.date}-${i}`} className="portal-activity-item">
                  <span className="portal-activity-dot"><RsIcon name="pin" size="xs" /></span>
                  <div>
                    <p className="portal-activity-text">{t.event}</p>
                    <p className="portal-activity-time">{t.date}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="portal-detail-empty">No activity yet</p>
          )}
        </div>
      </div>

      <div className="portal-deal-detail-actions">
        <button type="button" className="portal-btn-primary" onClick={() => onChat(deal)}>
          {listMode === 'browse' ? 'Contact Admin' : 'Chat'}
        </button>
        {role === 'LP' && listMode === 'browse' && !deal.isParticipant && (
          <button type="button" className="portal-btn-view" onClick={() => onJoin(deal.id)}>Express Interest</button>
        )}
      </div>
    </div>
  )
}

export const PORTAL_BROWSE_FILTERS = [
  { id: 'all', label: 'All Deals' },
  { id: 'fund', label: 'Fund', match: (d) => /fund|vc|pe/i.test(d.industry || '') },
  { id: 'direct', label: 'Direct', match: (d) => /direct|series|startup/i.test(d.industry || '') },
  { id: 'coinvest', label: 'Co-invest', match: (d) => /co/i.test(d.industry || '') },
  { id: 'secondary', label: 'Secondary', match: (d) => !/fund|vc|pe|direct|series|startup|co/i.test(d.industry || '') },
]
